import type { TranslationKey } from "@/modules/i18n/components/types"
import type { I18nLocale } from "@/modules/i18n/types"
import type { SelectedCcFiltersParamValue } from "@/modules/jobAds/client/hooks/useCcFilters"

export const DEFAULT_RADIUS_KM = 30
export const DEFAULT_PRECISION_KM = 5
export const RADIUS_KM_OPTIONS = [5, 10, 20, DEFAULT_RADIUS_KM, 50]
export const LOCALE_NONE = "none"
export const DEBOUNCE_DELAY = 500
export type StudyLevel =
  | "STUDY_LEVEL_BACHELOR"
  | "STUDY_LEVEL_DOCTORATE"
  | "STUDY_LEVEL_IRRELEVANT"
  | "STUDY_LEVEL_MASTER"
  | "STUDY_LEVEL_SHORT_CYCLE_TERTIARY_EDUCATION"
  | "STUDY_LEVEL_UNSPECIFIED"

/**
 * Proto study level from https://github.com/jobteaser/proto/blob/master/jobteaser/ontology/v1/study_level.proto
 * Be careful to keep it up to date according to the proto. If broken, this can impact the job offers search
 * query which depends on this enum.
 *
 * TODO: remove this associated table (& his logic) when this ticket is DONE : https://jobteaser.atlassian.net/browse/DUX-3561
 */
export const PROTO_STUDY_LEVELS = {
  STUDY_LEVEL_BACHELOR: 3, // level 6 in ISCED and of EQF
  STUDY_LEVEL_DOCTORATE: 5, // level 8 in ISCED and of EQF
  STUDY_LEVEL_IRRELEVANT: 1, // null value for job ad which does not depend on study level. It is the case for student jobs.
  STUDY_LEVEL_MASTER: 4, // level 7 in ISCED and of EQF
  STUDY_LEVEL_SHORT_CYCLE_TERTIARY_EDUCATION: 2, // level 5 in ISCED and EQF. Include Bac+2/3 like BTS, DUT, Real-oder Hauptschulabschluss, Abitur, Fachabitur, Ausbildung
  STUDY_LEVEL_UNSPECIFIED: 0,
} as const

export const STUDY_LEVEL_FROM_SEARCH_PARAMS_DICTIONARY = {
  "1": "irrelevant",
  "2": "short_cycle_tertiary_education",
  "3": "bachelor",
  "4": "master",
  "5": "doctorate",
  "6": "secondary_education_level",
} as const

export const MAX_DISPLAYED_CC_FILTER_TAGS = 10

/**
 * If you're adding new facets, respect this format <ES_FACET_NAME>: <algolia_facet_name>
 */
export const ALGOLIA_FACETS = {
  CANDIDACY_TYPE: "candidacy_type",
  COMPANY_BUSINESS_TYPE: "company.business_type",
  COMPANY_IDS: "company.id",
  COMPANY_SECTORS: "company.sectors.codenames",
  CONTRACT_DURATION: "contract.duration",
  CONTRACT_TYPE: "contract.type.original_label",
  CREATED_BY_STUDENT_OR_ALUMNI: "created_by_student_or_alumni",
  CURRICULUM_IDS: "curriculum_uuids",
  EXCLUSIVE: "exclusive",
  JOB_WITH_IMPACT: "job_with_impact",
  LOCALE: "locale",
  PARTNER_SCHOOL_IDS: "partner_school_uuids",
  POSITION_CATEGORY_UUID: "position_category.uuid",
  PUBLICATION_DATE: "activated_at",
  REMOTE_TYPES: "remote_type_facet",
  SCHOOL_IDS: "school_uuids",
  SCORE: "publication_type.score",
  START_DATE: "start_date",
  STUDY_LEVELS: "study_levels.id",
  TAGS: "school_tags.<school_uuid>.alternative_labels",
  WORK_EXPERIENCE_CODE: "work_experience_code",
  /**
   * OPTIMIZE https://jobteaser.atlassian.net/browse/DUX-4235
   * find a better way to isolate the locations key since it's not a facet
   */
  // eslint-disable-next-line perfectionist/sort-objects
  LOCATIONS: "locations.<location_type>",
} as const

export const ALGOLIA_OPTIONAL_FILTERS = {
  CONTRACT_TYPE: {
    facetName: ALGOLIA_FACETS.CONTRACT_TYPE,
    score: 2,
  },
  LOCALE: {
    facetName: ALGOLIA_FACETS.LOCALE,
    score: 3,
  },
  LOCATIONS: {
    facetName: ALGOLIA_FACETS.LOCATIONS,
    score: 3,
  },
  PARTNER_SCHOOL_IDS: {
    facetName: ALGOLIA_FACETS.PARTNER_SCHOOL_IDS,
    score: 2,
  },
  POSITION_CATEGORY_UUID: {
    facetName: ALGOLIA_FACETS.POSITION_CATEGORY_UUID,
    score: 3,
  },
  PUBLICATION_TYPE_SCORE: {
    facetName: "publication_type.score",
    score: 2,
  },
  STUDY_LEVELS: {
    facetName: ALGOLIA_FACETS.STUDY_LEVELS,
    score: 1,
  },
}

export const ALGOLIA_OPTIONAL_FILTERS_V2 = {
  CONTRACT_TYPE: {
    facetName: ALGOLIA_FACETS.CONTRACT_TYPE,
    score: 2,
  },
  LOCALE: {
    facetName: ALGOLIA_FACETS.LOCALE,
    score: 3,
  },
  LOCATIONS: {
    facetName: ALGOLIA_FACETS.LOCATIONS,
    score: 3,
  },
  PARTNER_SCHOOL_IDS: {
    facetName: ALGOLIA_FACETS.PARTNER_SCHOOL_IDS,
    score: 1,
  },
  POSITION_CATEGORY_UUID: {
    facetName: ALGOLIA_FACETS.POSITION_CATEGORY_UUID,
    score: 2,
  },
  PUBLICATION_TYPE_SCORE: {
    facetName: "publication_type.score",
    score: 2,
  },
  STUDY_LEVELS: {
    facetName: ALGOLIA_FACETS.STUDY_LEVELS,
    score: 3,
  },
}

export const ES_FACETS = Object.keys(ALGOLIA_FACETS).map(algoliaFacetKey => algoliaFacetKey.toLowerCase())

export const ALGOLIA_ATTRIBUTES_TO_RETRIEVE: string[] = [
  "activated_at",
  "candidacy_type",
  "company.id",
  "company.logo_url",
  "company.name",
  "contract.duration_max",
  "contract.duration_min",
  "contract.type.original_label",
  "created_by_student_or_alumni",
  "exclusive",
  "job_with_impact",
  "locations",
  "partner_school_uuids",
  "premium",
  "private_offer",
  "publication_type",
  "slug",
  "start_date",
  "title",
  "uuid",
]

export const PUBLICATION_TYPES = {
  FLASH_OR_SPONSORED: 1,
  FREE: 4,
  PREMIUM_SLOT: 2,
  STANDARD_SLOT: 3,
}

export const SEARCH_ENGINE_VERSIONS = {
  ALGOLIA: 2,
  ELASTIC_SEARCH: 1,
}

export const RESULTS_SORT_OPTIONS_MAPPING = {
  RECENCY: process.env.NEXT_PUBLIC_ALGOLIA_JOB_ADS_BY_DATE_DESC_INDEX_NAME,
  RELEVANCE: process.env.NEXT_PUBLIC_ALGOLIA_JOB_ADS_INDEX_NAME,
} as const

export const DEFAULT_RESULTS_SORT_OPTION = "relevance"

export const PRIVATE_OFFER_PATH = "/backend/recruiter_profile/new"
export const PRIVATE_OFFER_PATH_RECRUITER = "/backend/job-offers"

// ID of the "other" school
export const OTHER_SCHOOL_ID = 2876

export const AND_OPERATOR = "AND"
export const OR_OPERATOR = "OR"
export const NOT_OPERATOR = "NOT"
export const NOT_OPERATOR_SUBSTITUTE = "-"

export const NETWORK_CC_FILTER_DICTIONARY: {
  readonly [K in SelectedCcFiltersParamValue]: TranslationKey<"job_ads_fo.client.search.cc_filter">
} = {
  created_by_student_or_alumni: "job_ads_fo.client.search.cc_filter.alumni_jobs.label",
  partner_career_center: "job_ads_fo.client.search.cc_filter.partner_jobs.label",
  partner_faculty_career_center: "job_ads_fo.client.search.cc_filter.faculty_partner_jobs.label",
  reserved: "job_ads_fo.client.search.cc_filter.targeted_jobs.label",
}

export const AVAILABLE_AUTOCOMPLETE_INDEX_LOCALES: I18nLocale[] = ["da", "de", "en", "es", "fi"]

export const CONTENT_BOOSTS_POSITION_IN_JOB_SEARCH = 6

export const CARREER_EXPLORER_COURSE_POSITION_IN_JOB_SEARCH = 12

export const PUBLICATION_DATE_FILTER_OPTIONS = [
  "no-time-limit",
  "last-month",
  "last-week",
  "last-48-hours",
  "last-24-hours",
]
